import {Component, OnInit} from '@angular/core';
import {QuestionOptions} from '../../../../../constants/question-options';
import {FormBuilder, FormControl, Validators} from '@angular/forms';
import {OfferFormBase} from '../offer-form-base/offer-form-base.component';
import {CustomValidators} from '../../../../../validator/custom-validator';
import {Offer} from '../../../../../model/offer/offer';
import {Store} from '@ngrx/store';
import {combineLatest, debounceTime, filter, map, ReplaySubject, startWith, take, takeUntil} from 'rxjs';
import {Ratio} from '../../../../../model/ratio';
import {selectDetailPageProposalTile} from '../../../../../store/proposal/selectors/proposal.selectors';
import {ProposalAppState} from '../../../../../store/proposal/reducers/proposal.reducer';
import {NumberParseService} from '../../../../../services/parser/number-parse.service';
import {getCurrencySymbol} from '@angular/common';

@Component({
    selector: 'app-premium-information-form',
    templateUrl: './premium-information-form.component.html',
    styleUrls: ['../../../../../../styles/form-input.scss', 'premium-information-form.component.scss'],
    providers: [{ provide: OfferFormBase, useExisting: PremiumInformationFormComponent }],
    standalone: false
})
export class PremiumInformationFormComponent extends OfferFormBase implements OnInit {
  Ratio = Ratio;
  public filterCtrlCurrency: FormControl<string> = new FormControl<string>('');
  public filteredOptionsCurrency: ReplaySubject<string[]> = new ReplaySubject<string[]>(1);

  currencyOptions: string[] = QuestionOptions.currency;
  QuestionOptions = QuestionOptions;
  insuredAmount$ = this.proposalStore.select(selectDetailPageProposalTile).pipe(map(proposal => proposal?.insuredAmount));
  annualSalesAmount$= this.proposalStore.select(selectDetailPageProposalTile).pipe(map(proposal => proposal?.annualSalesAmount));
  formGroup = this.fb.group({
    selected: 0,
    premium: new FormControl<number>(null, [Validators.required, CustomValidators.validNumber()]),
    premiumInPercent: new FormControl<number>(null, [Validators.required, CustomValidators.validNumber({
      allowZero: true
    })]),
    premiumInPerThousand: new FormControl<number>(null, [Validators.required, CustomValidators.validNumber({
      allowZero: true
    })]),
    expectedTotalPremium: new FormControl<number>(null, [Validators.required, CustomValidators.validNumber()]),
    advancePremium: new FormControl<number>(null, [CustomValidators.validNumber({
      allowZero: true
    })]),
    minimumPremium: new FormControl<number>(null, [CustomValidators.validNumber({
      allowZero: true
    })]),
    currency: new FormControl<string>('EUR', [Validators.required]),
    deductibleAmountAbsolut: new FormControl<number>(null, [Validators.required, CustomValidators.validNumber({
      allowZero: true
    })]),
    deductiblePercent: new FormControl<number>(null, Validators.required),
    deductibleCurrency: new FormControl<string>('EUR', Validators.required),
    typeOfDeductible: new FormControl<string>(QuestionOptions.typeOfDeductible[0], Validators.required),
    offerDescription: new FormControl<string>(''),
    referenceValue: new FormControl<string>(QuestionOptions.referenceValues[0], Validators.required),
  });

  constructor(private readonly fb: FormBuilder,
              public proposalStore: Store<ProposalAppState>) {
    super();
  }

  ngOnInit(): void {
    this.formGroup.controls.selected.valueChanges.pipe(startWith(this.selected),
      takeUntil(this.destroy$)).subscribe(value => {
        if (value != 0) {
          this.formGroup.controls.premium.disable();
        } else {
          this.formGroup.controls.premium.enable();
        }
        if (value != 1) {
          this.formGroup.controls.premiumInPercent.disable();
        } else {
          this.formGroup.controls.premiumInPercent.enable();
        }
        if (value != 2) {
          this.formGroup.controls.premiumInPerThousand.disable();
        } else {
          this.formGroup.controls.premiumInPerThousand.enable();
        }
      }
    );
    this.setupPremiumControl();
    this.setupDeductible();
    this.filteredOptionsCurrency.next(this.currencyOptions);
    this.filterCtrlCurrency.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.filterCurrencies(this.filterCtrlCurrency.value, this.currencyOptions, this.filteredOptionsCurrency);
      });


  }

  filterCurrencies(search: string, options, filtered) {
    if (!options) {
      return;
    }
    if (!search) {
      filtered.next(options.slice());
      return;
    } else {
      search = search.toLowerCase();
    }
    filtered.next(
      options.filter(country => country.toLowerCase().indexOf(search) > -1)
    );
  }

  setupPremiumControl() {
    const premiumValueChange$ = this.formGroup.controls.premium.valueChanges
      .pipe(debounceTime(100),
        filter(() => this.formGroup.controls.premium.valid));
    combineLatest([premiumValueChange$, this.insuredAmount$, this.annualSalesAmount$])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([value, insuredAmount, annualSalesAmount]) => {
        if (this.formGroup.value.referenceValue == QuestionOptions.referenceValues[2]) {
          this.formGroup.controls.premiumInPercent.setValue(value / annualSalesAmount);
          this.formGroup.controls.premiumInPerThousand.setValue(value / annualSalesAmount);
        } else {
          this.formGroup.controls.premiumInPercent.setValue(value / insuredAmount);
          this.formGroup.controls.premiumInPerThousand.setValue(value / insuredAmount);
        }
        this.formGroup.controls.expectedTotalPremium.setValue(value);
        this.dispatchInputEventExpectedTotalPremium();
        this.dispatchInputEventPercent();
        this.dispatchInputEventPerThousand();
      });

    const premiumPercentChange = this.formGroup.controls.premiumInPercent.valueChanges.pipe(debounceTime(100),
      filter(() => this.formGroup.controls.premiumInPercent.valid));
    combineLatest([premiumPercentChange, this.insuredAmount$, this.annualSalesAmount$]).pipe(takeUntil(this.destroy$))
      .subscribe(([value, insuredAmount, annualSalesAmount]) => {
        if (this.formGroup.value.referenceValue === QuestionOptions.referenceValues[2]) {
          this.formGroup.controls.premium.setValue(value * annualSalesAmount);
          this.formGroup.controls.expectedTotalPremium.setValue(value * annualSalesAmount);
        } else {
          this.formGroup.controls.premium.setValue(value * insuredAmount);
          this.formGroup.controls.expectedTotalPremium.setValue(value * insuredAmount);
        }
        this.formGroup.controls.premiumInPerThousand.setValue(value);
        this.dispatchInputEventExpectedTotalPremium();
        this.dispatchInputEventPremium();
        this.dispatchInputEventPerThousand();
      });
    const premiumPerThousand = this.formGroup.controls.premiumInPerThousand.valueChanges.pipe(debounceTime(100),
      filter(() => this.formGroup.controls.premiumInPerThousand.valid));
    combineLatest([premiumPerThousand, this.insuredAmount$, this.annualSalesAmount$]).pipe(takeUntil(this.destroy$))
      .subscribe(([value, insuredAmount, annualSalesAmount]) => {
        if (this.formGroup.value.referenceValue === QuestionOptions.referenceValues[2]) {
          this.formGroup.controls.premium.setValue(value * annualSalesAmount);
          this.formGroup.controls.expectedTotalPremium.setValue(value * annualSalesAmount);
        } else {
          this.formGroup.controls.premium.setValue(value * insuredAmount);
          this.formGroup.controls.expectedTotalPremium.setValue(value * insuredAmount);
        }
        this.formGroup.controls.premiumInPercent.setValue(value);
        this.dispatchInputEventExpectedTotalPremium();
        this.dispatchInputEventPremium();
        this.dispatchInputEventPercent();
      });
  }

  dispatchInputEventExpectedTotalPremium() {
    const input = document.querySelector('input[formControlName=\'expectedTotalPremium\']');
    input.dispatchEvent(new Event('convertNumber'));
  }

  dispatchInputEventPremium() {
    const input = document.querySelector('input[formControlName=\'premium\']');
    if (input) {
      input.dispatchEvent(new Event('convertNumber'));
    }
  }

  dispatchInputEventPercent() {
    const input = document.querySelector('input[formControlName=\'premiumInPercent\']');
    if (input) {
      input.dispatchEvent(new Event('convertNumber'));
    }
  }

  dispatchInputEventPerThousand() {
    const input = document.querySelector('input[formControlName=\'premiumInPerThousand\']');
    if (input) {
      input.dispatchEvent(new Event('convertNumber'));
    }

  }

  updateOffer(offer: Offer): void {
    offer.premium = NumberParseService.calcCutOffNumber(2, this.formGroup.getRawValue().premium, true);
    offer.expectedTotalPremium = this.formGroup.value.expectedTotalPremium;
    offer.advancePremium = this.formGroup.value.advancePremium;
    offer.minimumPremium = this.formGroup.value.minimumPremium;
    offer.currency = this.formGroup.value.currency;
    offer.deductibleAbsoluteAmount = NumberParseService.calcCutOffNumber(2, this.formGroup.value.deductibleAmountAbsolut, true);
    offer.deductibleCurrency = this.formGroup.value.currency;
    offer.typeOfDeductible = this.formGroup.value.typeOfDeductible;
    offer.offerDescription = this.formGroup.value.offerDescription;
    offer.referenceValue = this.formGroup.value.referenceValue;
    if (this.formGroup.value.selected == 1) {
      offer.premiumInPercent = this.formGroup.value.premiumInPercent;
    }
    if (this.formGroup.value.selected == 2) {
      offer.premiumPerThousand = this.formGroup.value.premiumInPerThousand;
    }
  }

  get selected() {
    return this.formGroup.value.selected;
  }


  setupDeductible() {
    const deductiblePercent = this.formGroup.controls.deductiblePercent.valueChanges.pipe(debounceTime(100),
      filter(() => this.formGroup.controls.deductiblePercent.valid));
    combineLatest([deductiblePercent, this.insuredAmount$]).pipe(takeUntil(this.destroy$)).subscribe(([value, insuredAmount]) => {
      this.formGroup.controls.deductibleAmountAbsolut.setValue(value * insuredAmount, {emitEvent: false, onlySelf: true});
      const input = document.querySelector('input[formControlName=\'deductibleAmountAbsolut\']');
      input.dispatchEvent(new Event('convertNumber'));
      this.formGroup.updateValueAndValidity();
    });

    const deductibleAbsolut = this.formGroup.controls.deductibleAmountAbsolut.valueChanges.pipe(debounceTime(100),
      filter(() => this.formGroup.controls.deductibleAmountAbsolut.valid));
    combineLatest([deductibleAbsolut, this.insuredAmount$]).pipe(takeUntil(this.destroy$)).subscribe(([value, insuredAmount]) => {
      this.formGroup.controls.deductiblePercent.setValue(value / insuredAmount, {emitEvent: false, onlySelf: true});
      const input = document.querySelector('input[formControlName=\'deductiblePercent\']');
      input.dispatchEvent(new Event('convertNumber'));
      this.formGroup.updateValueAndValidity();
    });

    this.formGroup.controls.typeOfDeductible.valueChanges.pipe(startWith(this.formGroup.value.typeOfDeductible),
      takeUntil(this.destroy$)).subscribe((value) => {
      if (QuestionOptions.typeOfDeductible[0] == value) {
        this.formGroup.controls.deductiblePercent.disable();
        this.formGroup.controls.deductibleAmountAbsolut.disable();
      } else {
        this.formGroup.controls.deductiblePercent.enable();
        this.formGroup.controls.deductibleAmountAbsolut.enable();
      }
    });
  }


  currencySymbol() {
    return getCurrencySymbol(this.formGroup.value.currency, 'narrow');
  }

  prefill(offer: Offer): void {
    this.insuredAmount$.pipe(take(1)).subscribe((insuredAmount) => {
      const deductibleAbsolute = offer.deductibleAbsoluteAmount || null;
      const deductiblePercent = (insuredAmount && deductibleAbsolute != null)
        ? deductibleAbsolute / insuredAmount
        : null;

      this.formGroup.patchValue({
        premium: offer.premium || null,
        premiumInPercent: offer.premiumInPercent || null,
        premiumInPerThousand: offer.premiumPerThousand || null,
        expectedTotalPremium: offer.expectedTotalPremium || null,
        advancePremium: offer.advancePremium || null,
        minimumPremium: offer.minimumPremium || null,
        currency: offer.currency || 'EUR',
        deductibleAmountAbsolut: deductibleAbsolute || null,
        deductiblePercent: deductiblePercent || null,
        deductibleCurrency: offer.deductibleCurrency || 'EUR',
        typeOfDeductible: offer.typeOfDeductible || QuestionOptions.typeOfDeductible[0],
        offerDescription: offer.offerDescription || '',
        referenceValue: offer.referenceValue || QuestionOptions.referenceValues[0],
      });
    });
  }

}
